import React, { useEffect, useRef, useState } from 'react'
import { getErrorMessage } from '@/utils/getErrorMessage'
import {
  Steps,
  Typography,
  Button,
  Breadcrumb,
  Layout,
  Divider,
  Flex,
  Spin,
  message,
} from 'antd'
import { useNavigate, useLocation, useParams, Outlet } from 'react-router-dom'
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons'
import {
  getDefaultState,
  getValidationErrors,
} from '@/components/FormBuilder/templates'
import { getFlow } from '@/api/lop/lead-flows/getFlow'
import { createFlow } from '@/api/lop/lead-flows/createFlow'
import { updateFlow } from '@/api/lop/lead-flows/updateFlow'
import { createSteps } from '@/api/lop/lead-flows/createStep'
import type { LeadFlowPayload } from '@/api/lop/lead-flows/createFlow'
import type { FlowStep, FlowBranding } from '@/data'
import { defaultFlowBranding } from '@/data'

const { Title, Text } = Typography
const { Content } = Layout

export interface FlowEditorContext {
  mode: 'edit' | 'create'
  flowId: string | null
  flowName: string
  setFlowName: (name: string) => void
  flowSlug: string
  setFlowSlug: (slug: string) => void
  selectedTemplate: string | null
  setSelectedTemplate: (template: string) => void
  linkedChatId: string | null
  setLinkedChatId: (id: string | null) => void
  getTemplateState: (slug: string) => FlowStep[]
  setTemplateState: (state: FlowStep[]) => void
  selectedPixelIds: number[]
  setSelectedPixelIds: (ids: number[]) => void
  downsellId: number | null
  setDowsellId: (id: number | null) => void
  branding: FlowBranding
  setBranding: (branding: FlowBranding) => void
  brandingEnabled: boolean
  setBrandingEnabled: (enabled: boolean) => void
  basePath: string
}

const steps = [
  { title: 'Form Information', path: 'form-info' },
  { title: 'Form Builder', path: 'form-builder' },
  { title: 'Chat', path: 'chat' },
  { title: 'Downsell', path: 'downsell' },
  { title: 'Pixels', path: 'pixels' },
]

const LeadFlowEditorContent: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { slug } = useParams<{ slug: string }>()

  const isEditMode = Boolean(slug)
  const basePath = isEditMode ? `/lead-flows/${slug}/edit` : '/lead-flows/new'

  const [loading, setLoading] = useState(isEditMode)
  const [saving, setSaving] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)
  const [flowName, setFlowName] = useState<string>('')
  const [flowSlug, setFlowSlug] = useState<string>('')
  const [linkedChatId, setLinkedChatId] = useState<string | null>(
    isEditMode ? null : '1',
  )
  const [templateStates, setTemplateStates] = useState<
    Record<string, FlowStep[]>
  >({})
  const [selectedPixelIds, setSelectedPixelIds] = useState<number[]>([])
  const [downsellId, setDowsellId] = useState<number | null>(
    isEditMode ? null : 1,
  )
  const [brandingEnabled, setBrandingEnabled] = useState(false)
  const [branding, setBranding] = useState<FlowBranding>({
    ...defaultFlowBranding,
  })

  const hasFetched = useRef(false)

  useEffect(() => {
    if (!slug || hasFetched.current) return
    hasFetched.current = true
    ;(async () => {
      try {
        setLoading(true)
        const flow = await getFlow(slug)
        if (!flow) return
        setSelectedTemplate(flow.template_slug)
        setFlowName(flow.name)
        setFlowSlug(flow.slug)
        setLinkedChatId(flow.chat_id != null ? String(flow.chat_id) : '1')
        const sortedSteps = structuredClone(flow.steps).sort(
          (a: FlowStep, b: FlowStep) =>
            a.ordinal != null && b.ordinal != null
              ? a.ordinal - b.ordinal
              : (a.id ?? 0) - (b.id ?? 0),
        )
        setTemplateStates({ [flow.template_slug]: sortedSteps })
        setSelectedPixelIds(
          flow.pixels?.map((p) => p.id) ?? flow.pixel_ids ?? [],
        )
        setDowsellId(flow.downsell_id ?? 1)
        if (flow.brand) {
          setBrandingEnabled(true)
          setBranding(flow.brand)
        }
      } catch (err) {
        message.error('Failed to load flow')
        console.error(err)
      } finally {
        setLoading(false)
      }
    })()
  }, [slug])

  const getTemplateState = (slug: string): FlowStep[] => {
    if (templateStates[slug] !== undefined) return templateStates[slug]
    return getDefaultState(slug)
  }

  const setTemplateState = (state: FlowStep[]) => {
    if (!selectedTemplate) return
    setTemplateStates((prev) => ({ ...prev, [selectedTemplate]: state }))
  }

  const mapStepsForApi = (steps: FlowStep[]): FlowStep[] => {
    return steps.map((step, index) => ({
      ...step,
      ordinal: index + 1,
      sub_headline: step.sub_headline || '',
      cta_button: step.cta_button || '',
    }))
  }

  const buildPayload = (): LeadFlowPayload => {
    const allSteps = selectedTemplate ? getTemplateState(selectedTemplate) : []
    // PUT endpoint requires id on every step — exclude new steps (created via createSteps)
    const stepsForApi = isEditMode
      ? allSteps.filter((s) => s.id != null)
      : allSteps
    return {
      name: flowName,
      slug: flowSlug,
      template_slug: selectedTemplate || '',
      steps: mapStepsForApi(stepsForApi),
      chat_id: linkedChatId != null ? Number(linkedChatId) : null,
      pixel_ids: selectedPixelIds,
      downsell_id: downsellId,
      ...(brandingEnabled ? { brand: branding } : {}),
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      if (isEditMode && slug) {
        await updateFlow(slug, buildPayload())
        message.success('Flow updated')
      } else {
        const created = await createFlow(buildPayload())
        message.success('Flow created')
        navigate(`/lead-flows/${created.slug}/edit/form-info`)
      }
    } catch (err) {
      const fallback = isEditMode
        ? 'Failed to update flow'
        : 'Failed to create flow'
      message.error(getErrorMessage(err, fallback))
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  // Determine current step based on URL path
  const currentStep = steps.findIndex((step) =>
    location.pathname.includes(`${basePath}/${step.path}`),
  )
  const activeStep = currentStep === -1 ? 0 : currentStep

  const handleStepChange = (step: number) => {
    navigate(`${basePath}/${steps[step].path}`)
  }

  const formBuilderErrors =
    activeStep === 1 && selectedTemplate
      ? getValidationErrors(
          selectedTemplate,
          getTemplateState(selectedTemplate),
        )
      : []

  const goBack = () => {
    if (activeStep > 0) {
      navigate(`${basePath}/${steps[activeStep - 1].path}`)
    }
  }

  const goNext = async () => {
    // When leaving the form builder step in edit mode, POST any new steps
    if (activeStep === 1 && isEditMode && slug && selectedTemplate) {
      const currentSteps = getTemplateState(selectedTemplate)
      const newSteps = currentSteps.filter((s) => s.id == null)
      if (newSteps.length > 0) {
        try {
          await createSteps(slug, newSteps)
          // Re-fetch flow to get server-assigned IDs for the new steps
          const flow = await getFlow(slug)
          if (flow) {
            const sortedSteps = structuredClone(flow.steps).sort(
              (a: FlowStep, b: FlowStep) =>
                a.ordinal != null && b.ordinal != null
                  ? a.ordinal - b.ordinal
                  : (a.id ?? 0) - (b.id ?? 0),
            )
            setTemplateStates((prev) => ({
              ...prev,
              [selectedTemplate]: sortedSteps,
            }))
          }
        } catch (err) {
          message.error(getErrorMessage(err, 'Failed to create steps'))
          console.error(err)
          return
        }
      }
    }
    if (activeStep < steps.length - 1) {
      navigate(`${basePath}/${steps[activeStep + 1].path}`)
    }
  }

  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    )
  }

  return (
    <div>
      <Flex vertical gap="middle" style={{ width: '100%' }}>
        <Breadcrumb
          items={[
            { title: 'Lead Flows', href: '/lead-flows' },
            { title: isEditMode ? `Edit: ${flowName}` : 'New Flow' },
          ]}
        />

        <Flex align="center" gap="middle">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/lead-flows')}
          />
          <Title level={3} style={{ margin: 0 }}>
            {isEditMode
              ? `Edit Lead Flow: ${flowName}`
              : 'Create New Lead Flow'}
          </Title>
        </Flex>

        <Flex gap="large" style={{ marginTop: 16 }}>
          <div
            style={{
              borderRight: '1px solid #f0f0f0',
              paddingRight: 24,
              minWidth: 200,
            }}
          >
            <Steps
              current={activeStep}
              onChange={handleStepChange}
              orientation="vertical"
              items={steps.map((step) => ({
                title: step.title,
              }))}
              style={{ position: 'sticky', top: 24 }}
            />
          </div>
          <Content style={{ flex: 1, minHeight: 400 }}>
            <Outlet
              context={{
                mode: isEditMode ? 'edit' : 'create',
                flowId: slug || null,
                flowName,
                setFlowName,
                flowSlug,
                setFlowSlug,
                selectedTemplate,
                setSelectedTemplate,
                linkedChatId,
                setLinkedChatId,
                getTemplateState,
                setTemplateState,
                selectedPixelIds,
                setSelectedPixelIds,
                downsellId,
                setDowsellId,
                branding,
                setBranding,
                brandingEnabled,
                setBrandingEnabled,
                basePath,
              }}
            />

            <Divider />

            <Flex justify="space-between" style={{ paddingTop: 16 }}>
              <Button
                onClick={goBack}
                disabled={activeStep === 0}
                icon={<ArrowLeftOutlined />}
              >
                Back
              </Button>

              {activeStep < steps.length - 1 ? (
                <Flex align="center" gap="small">
                  {formBuilderErrors.length > 0 && (
                    <Text type="danger" style={{ fontSize: 13 }}>
                      {formBuilderErrors[0]}
                    </Text>
                  )}
                  <Button
                    type="primary"
                    onClick={goNext}
                    disabled={formBuilderErrors.length > 0}
                  >
                    Next
                    <ArrowRightOutlined />
                  </Button>
                </Flex>
              ) : (
                <Button type="primary" onClick={handleSave} loading={saving}>
                  {isEditMode ? 'Save Changes' : 'Create Flow'}
                </Button>
              )}
            </Flex>
          </Content>
        </Flex>
      </Flex>
    </div>
  )
}

export const LeadFlowEditor: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  return <LeadFlowEditorContent key={slug ?? 'new'} />
}
